import { useTranslation } from "react-i18next";
import { Check, Loader2, Trash2 } from "lucide-react";
import { Button } from "~/atoms/button";
import { Checkbox } from "~/atoms/checkbox";
import { Input } from "~/atoms/input";
import { Spinner } from "~/atoms/spinner";
import { TableCell, TableRow } from "~/atoms/table";
import { type Option } from "~/molecules/ControlledCombobox";
import { ComboboxCell } from "~/molecules/onboard-documents/ComboboxCell";
import {
  type DraftRow,
  type RowPatch,
  isDraftRowValid,
} from "~/helpers/draftRows";
import { cn } from "~/lib/utils";

export type DraftRowHoverCol = "tier" | "category";

interface DraftTableRowProps {
  row: DraftRow;
  isSelected: boolean;
  isCreating: boolean;
  hasDuplicate: boolean;
  tierOptions: Option[] | undefined;
  categoryOptions: Option[] | undefined;
  onToggleSelection: () => void;
  onUpdate: (patch: RowPatch, opts?: { bulk?: boolean }) => void;
  onCreate: () => void;
  onRemove: () => void;
  onHoverCell: (col: DraftRowHoverCol) => void;
  onHoverLeave: () => void;
  isBulkHighlighted: (col: DraftRowHoverCol) => boolean;
}

export const DraftTableRow = ({
  row,
  isSelected,
  isCreating,
  hasDuplicate,
  tierOptions,
  categoryOptions,
  onToggleSelection,
  onUpdate,
  onCreate,
  onRemove,
  onHoverCell,
  onHoverLeave,
  isBulkHighlighted,
}: DraftTableRowProps) => {
  const { t } = useTranslation();

  const disabled = row.status === "creating";
  const isFailed = row.status === "failed";

  if (row.status === "created") {
    const tierLabel = tierOptions?.find((o) => o.value === row.tierId)?.label;
    const categoryLabel = categoryOptions?.find(
      (o) => o.value === row.categoryId,
    )?.label;
    return (
      <TableRow className="bg-success/4">
        <TableCell>
          <Check className="text-primary size-4" />
        </TableCell>
        <TableCell>{row.title}</TableCell>
        <TableCell>{tierLabel}</TableCell>
        <TableCell>{categoryLabel}</TableCell>
        <TableCell>
          <div className="text-primary flex items-center justify-end text-sm font-bold">
            {t(($) => $.library.onboardDocuments.steps.createDrafts.created)}
          </div>
        </TableCell>
      </TableRow>
    );
  }

  const requiredMessage = t(
    ($) => $.library.onboardDocuments.steps.createDrafts.requiredField,
  );
  const placeholder = t(($) => $.commons.forms.selectAnOption);

  return (
    <TableRow className={cn(isFailed && "bg-destructive/10")}>
      <TableCell>
        <div className="flex size-4 items-center justify-center">
          {row.status === "creating" ? (
            <Spinner className="size-4" />
          ) : (
            <Checkbox
              checked={isSelected}
              onCheckedChange={onToggleSelection}
              aria-label={t(($) => $.commons.buttons.selectItem, {
                name: row.title || row.file.name,
              })}
            />
          )}
        </div>
      </TableCell>
      <TableCell>
        <Input
          value={row.title}
          onChange={(e) => onUpdate({ title: e.target.value })}
          disabled={disabled}
        />
        {hasDuplicate && (
          <p className="text-warning text-xs">
            {t(
              ($) =>
                $.library.onboardDocuments.steps.createDrafts.duplicateWarning,
            )}
          </p>
        )}
      </TableCell>
      <TableCell>
        <ComboboxCell
          value={row.tierId}
          onChange={(value) => onUpdate({ tierId: value }, { bulk: true })}
          options={tierOptions}
          placeholder={placeholder}
          disabled={disabled || !tierOptions}
          hasError={isSelected && !row.tierId}
          errorMessage={requiredMessage}
          highlighted={isBulkHighlighted("tier")}
          onHover={() => onHoverCell("tier")}
          onHoverLeave={onHoverLeave}
        />
      </TableCell>
      <TableCell>
        <ComboboxCell
          value={row.categoryId}
          onChange={(value) => onUpdate({ categoryId: value }, { bulk: true })}
          options={categoryOptions}
          placeholder={placeholder}
          disabled={disabled || !categoryOptions}
          hasError={isSelected && !row.categoryId}
          errorMessage={requiredMessage}
          highlighted={isBulkHighlighted("category")}
          onHover={() => onHoverCell("category")}
          onHoverLeave={onHoverLeave}
        />
      </TableCell>
      <TableCell>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={onCreate}
              disabled={disabled || isCreating || !isDraftRowValid(row)}
            >
              {row.status === "creating" && (
                <Loader2 className="animate-spin" />
              )}
              {t(($) => $.library.onboardDocuments.steps.createDrafts.create)}
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onRemove}
              disabled={disabled}
              aria-label={t(
                ($) => $.library.onboardDocuments.steps.createDrafts.removeRow,
              )}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
          {isFailed && (
            <p className="text-destructive text-xs">
              {t(
                ($) =>
                  $.library.onboardDocuments.steps.createDrafts.failedToCreate,
              )}
            </p>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};
